// Task notes panel (week-one feedback §7) — the same component on the Tasks
// page's right-hand column and inside a Modal everywhere else. View mode
// renders the plain-text notes format (headings, checkboxes, bullets, sum
// lines); Edit mode is the raw textarea, autosaved on blur. Every change
// PATCHes tasks.notes, so the stored format stays plain and portable.

import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { reportError } from '../lib/errorEvents';
import { checkProgress, NOTES_MAX_LENGTH, parseNotes, toggleCheck, type NotesLine } from '../../shared/taskNotes';
import { formatDayMonth, Seg } from './primitives';
import type { TaskRowData } from './TaskRow';

export const PRIORITY_LABELS: Record<number, string> = { 1: 'High ⚑', 2: 'Normal', 3: 'Low' };

/** A calc line with nothing but a number after the `=` shows a bare "=" —
    echoing "14320 = 14,320" back at Henry reads as noise. */
const BARE_NUMBER = /^\$?[\d,]+(\.\d+)?$/;

/** Everything the panel does to a task — mode, notes text, the autosave and
    its guards, checklist ticking, the title rename. Lifted out of the panel
    so the mobile task sheet (mobile overhaul §0.6, components/TaskSheet.tsx)
    runs the same save logic instead of a second copy of it. The ⌘↩ shortcut
    stays with the desktop panel: on mobile the sheet header's "Done" is it. */
export function useTaskPanel(task: TaskRowData, onChanged: () => void) {
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [text, setText] = useState(task.notes ?? '');
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(task.title);
  // True from the first keystroke until the blur that saves it — stops the
  // parent's refresh landing mid-sentence and rewinding the textarea.
  const typing = useRef(false);
  // Saves in flight. While any is open the incoming task.notes is behind
  // what's on screen, so it must not resync over the tick just made.
  const saving = useRef(0);
  const textRef = useRef(text);
  textRef.current = text;

  // A different task in the panel starts fresh in view mode; an update to
  // the same task's notes (our own save landing, or a background refresh)
  // only re-syncs the text when Henry isn't mid-keystroke.
  useEffect(() => {
    typing.current = false;
    setMode('view');
    setText(task.notes ?? '');
  }, [task.id]);
  useEffect(() => {
    if (!typing.current && saving.current === 0) setText(task.notes ?? '');
  }, [task.notes]);

  const save = async (next: string) => {
    typing.current = false;
    if (next === (task.notes ?? '')) return;
    saving.current += 1;
    try {
      await api(`/api/admin/tasks/${task.id}`, { method: 'PATCH', json: { notes: next } }).catch((error) =>
        reportError(error, 'Could not save the notes.'),
      );
    } finally {
      saving.current -= 1;
    }
    onChanged();
  };

  const commitTitle = () => {
    setEditingTitle(false);
    const t = titleDraft.trim();
    if (!t || t === task.title) return;
    api(`/api/admin/tasks/${task.id}`, { method: 'PATCH', json: { title: t } })
      .catch((error) => reportError(error, 'Could not rename the task.'))
      .finally(onChanged);
  };

  const toggle = (index: number) => {
    const next = toggleCheck(text, index);
    if (next === text) return;
    setText(next);
    void save(next);
  };

  const progress = checkProgress(text);

  return {
    mode,
    setMode,
    text,
    setText,
    save,
    commitTitle,
    toggle,
    editingTitle,
    setEditingTitle,
    titleDraft,
    setTitleDraft,
    progress,
    textRef,
    typing,
  };
}

export function TaskPanel({
  task,
  today,
  onChanged,
  onClose,
}: {
  task: TaskRowData;
  today: string;
  onChanged: () => void;
  onClose: () => void;
}) {
  const {
    mode,
    setMode,
    text,
    setText,
    save,
    commitTitle,
    toggle,
    editingTitle,
    setEditingTitle,
    titleDraft,
    setTitleDraft,
    progress,
    textRef,
    typing,
  } = useTaskPanel(task, onChanged);

  // ⌘↩ (⌃↩ on a keyboard without it) saves whatever is open and closes.
  // Deliberately re-bound every render so the handler always closes over the
  // current save/onClose — the textarea's own text comes off textRef.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || !(e.metaKey || e.ctrlKey)) return;
      e.preventDefault();
      void save(textRef.current).then(onClose);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const dueLabel = !task.due_date
    ? 'No due date'
    : task.due_date === today
      ? 'Today'
      : formatDayMonth(task.due_date);

  return (
    <div className="task-panel">
      <div className="panel-head">
        {editingTitle ? (
          <input
            className="inline-input panel-title-input"
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitTitle();
              if (e.key === 'Escape') setEditingTitle(false);
            }}
            onBlur={commitTitle}
            autoFocus
          />
        ) : (
          <div
            className="panel-title editable"
            onClick={() => {
              setTitleDraft(task.title);
              setEditingTitle(true);
            }}
          >
            {task.title}
          </div>
        )}
        <span className="panel-close" title="Close (Esc)" onClick={onClose}>
          ×
        </span>
        <div className="panel-meta">
          <span className="project-tag">{task.project_name ?? 'No project'}</span>
          <span className="chip">{dueLabel}</span>
          <span className="chip">{PRIORITY_LABELS[task.priority] ?? 'Normal'}</span>
        </div>
      </div>

      <div className="panel-body">
        <div className="panel-label">
          Notes
          {progress.total > 0 && (
            <span className="notes-progress">
              {progress.done} of {progress.total} done
            </span>
          )}
          <span className="seg-wrap">
            <Seg
              options={[
                { value: 'view', label: 'View' },
                { value: 'edit', label: 'Edit' },
              ]}
              value={mode}
              onChange={setMode}
            />
          </span>
        </div>

        {mode === 'edit' ? (
          <textarea
            className="notes-textarea"
            maxLength={NOTES_MAX_LENGTH}
            value={text}
            placeholder={'## Kit\n- [ ] Batteries charged\n- [ ] Format cards\nGST = 14320 × 10%'}
            onChange={(e) => {
              typing.current = true;
              setText(e.target.value);
            }}
            onBlur={() => void save(text)}
            autoFocus
          />
        ) : text.trim() === '' ? (
          <div className="notes-empty" onClick={() => setMode('edit')}>
            No notes yet — click to add a checklist
          </div>
        ) : (
          <div className="notes-view">
            {parseNotes(text).map((line) => (
              <NotesLineView key={line.index} line={line} onToggle={toggle} />
            ))}
          </div>
        )}
      </div>

      <div className="panel-foot">
        <span>Autosaves</span>
        <span>
          {mode === 'edit'
            ? `${text.length.toLocaleString('en-AU')} / ${NOTES_MAX_LENGTH.toLocaleString('en-AU')}`
            : 'Click a box to tick'}
        </span>
        <span>⌘↩ done</span>
      </div>
    </div>
  );
}

export function NotesLineView({ line, onToggle }: { line: NotesLine; onToggle: (index: number) => void }) {
  switch (line.kind) {
    case 'heading':
      return <div className="h">{line.text}</div>;
    case 'check':
      return (
        <div className={`ck${line.checked ? ' done' : ''}`} onClick={() => onToggle(line.index)}>
          <span className="box" />
          <span className="t">{line.text}</span>
        </div>
      );
    case 'bullet':
      return (
        <div className="bl">
          <span className="dot">–</span>
          <span>{line.text}</span>
        </div>
      );
    case 'calc':
      // An expression that didn't parse falls back to its raw line, so a
      // half-typed sum never turns into a wrong number.
      if (line.error) return <div className="tx">{line.raw}</div>;
      return (
        <div className="calc">
          <span>{line.label}</span>
          <span className="eq">{BARE_NUMBER.test(line.expression) ? '=' : `= ${line.expression}`}</span>
          <span className="res">{line.display}</span>
        </div>
      );
    default:
      return <div className="tx">{line.text === '' ? ' ' : line.text}</div>;
  }
}
